import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import icons from '../config/icons.config'
import { useModal } from '../contexts/ModalContext'
import { useSignUp } from '../hooks/auth/useSignUp'
import { ScreenPath } from '../navigation'
import { logError } from '../util/errorLogger'
import styles from '../ui/auth/AuthForm.module.css'
import AppIcon from '../ui/icons/AppIcon'
import SimplePageLayout from '../ui/layout/SimplePageLayout'
import Button from '../ui/primitives/Button'
import TextField from '../ui/primitives/TextField'

export interface SignUpForm {
  firstName: string
  lastName: string
  email: string
  confirmEmail: string
  password: string
  confirmPassword: string
  acceptedPrivacyPolicy: boolean
  acceptedWaiver: boolean
}

export default function SignUpScreen() {
  const navigate = useNavigate()
  const { showSnackbar } = useModal()
  const location = useLocation()
  const from = location.state?.from
  const { mutateAsync: signUp, isPending } = useSignUp()
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignUpForm>({
    defaultValues: { acceptedPrivacyPolicy: false, acceptedWaiver: false },
  })
  const email = watch('email')
  const password = watch('password')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  async function onSubmit(data: SignUpForm) {
    try {
      await signUp({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
      })
      navigate(from ?? ScreenPath.home, { replace: true })
    } catch (error: unknown) {
      void logError(error, { screen: 'SignUp' })
      const message = error instanceof Error ? error.message : 'Sign up failed'
      showSnackbar('signup-error', message, 'error')
    }
  }

  return (
    <SimplePageLayout title="Sign up">
      <div className={styles.card}>
        <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
          <TextField
            label="First name"
            autoFocus
            error={errors.firstName?.message}
            {...register('firstName', { required: 'First name is required' })}
          />
          <TextField
            label="Last name"
            error={errors.lastName?.message}
            {...register('lastName', { required: 'Last name is required' })}
          />
          <TextField
            label="Email"
            type="email"
            error={errors.email?.message}
            {...register('email', { required: 'Email is required' })}
          />
          <TextField
            label="Confirm email"
            type="email"
            error={errors.confirmEmail?.message}
            {...register('confirmEmail', {
              required: 'Please confirm your email',
              validate: (value) => value === email || 'Emails do not match',
            })}
          />
          <TextField
            label="Password"
            type={showPassword ? 'text' : 'password'}
            error={errors.password?.message}
            endAdornment={
              <button
                type="button"
                className={styles.passwordToggle}
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label="Toggle password visibility"
              >
                <AppIcon icon={showPassword ? icons.eyeOff : icons.eye} size={18} />
              </button>
            }
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 6, message: 'Password must be at least 6 characters long' },
            })}
          />
          <TextField
            label="Confirm password"
            type={showConfirmPassword ? 'text' : 'password'}
            error={errors.confirmPassword?.message}
            endAdornment={
              <button
                type="button"
                className={styles.passwordToggle}
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                aria-label="Toggle confirm password visibility"
              >
                <AppIcon icon={showConfirmPassword ? icons.eyeOff : icons.eye} size={18} />
              </button>
            }
            {...register('confirmPassword', {
              required: 'Please confirm your password',
              validate: (value) => value === password || 'Passwords do not match',
            })}
          />

          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              {...register('acceptedPrivacyPolicy', {
                required: 'You must accept the privacy policy to continue',
              })}
            />
            <span>
              I accept the{' '}
              <a href="https://relentless2800.com/privacy" target="_blank" rel="noreferrer">
                Privacy Policy
              </a>
            </span>
          </label>
          {errors.acceptedPrivacyPolicy && (
            <span className={styles.fieldError}>{errors.acceptedPrivacyPolicy.message}</span>
          )}

          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              {...register('acceptedWaiver', {
                required: 'You must accept the waiver to continue',
              })}
            />
            <span>
              I accept the{' '}
              <a href="https://relentless2800.com/waiver" target="_blank" rel="noreferrer">
                Waiver
              </a>
            </span>
          </label>
          {errors.acceptedWaiver && (
            <span className={styles.fieldError}>{errors.acceptedWaiver.message}</span>
          )}

          <p className={styles.hint}>
            By signing up, you&apos;re opting in to receive Relentless communications.
          </p>

          <div className={styles.actions}>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Creating account…' : 'Sign up'}
            </Button>
            <p className={styles.linkRow}>
              Already have an account?{' '}
              <button
                type="button"
                className={styles.linkButton}
                onClick={() =>
                  navigate(ScreenPath.login, {
                    state: { from: location.state?.from },
                    replace: true,
                  })
                }
              >
                Log in
              </button>
            </p>
          </div>
        </form>
      </div>
    </SimplePageLayout>
  )
}
